import React, { useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';

import get from 'lodash/get';
import uuid from 'uuid/v4';

import styled from 'styled-components';

import { getInstance as getBootstrap } from 'utils/bootstrap';

import LinkedInIcon from 'common/icons/LinkedIn';
import { colorLinkedin, colorLinkedinHover } from '../../../../../../styles/palette';

import { FormIdContext } from '../../../FormIdContext';
import { linkedInReceiveMessage } from '../../../../epics/socialRsvp';

const BASE_COMPONENT_NAME = 'LinkedIn';
const LINKED_IN_LABEL = 'Sign in with LinkedIn';
const BLANK = '_blank';
const WINDOW_DIMENTIONS = 'width=600, height=600';

const LinkedInButton = styled.button.attrs({
    type: 'button',
})`
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: 52px;
    font-weight: normal;
    font-family: Helvetica Neue;
    font-size: 14px;
    border-radius: 4px;
    outline: none;
    background-color: ${colorLinkedin};
    color: ${({ theme }) => theme.palette.grayscale.white};
    cursor: pointer;
    &:hover {
        background-color: ${colorLinkedinHover};
    }
    &:focus {
        background-color: ${colorLinkedinHover};
    }
    &:active {
        background-color: ${colorLinkedinHover};
    }
`;

const LinkedInIconWrapper = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    fill: ${({ theme }) => theme.palette.grayscale.white};
    height: 50px;
    width: 50px;
    margin-right: 1rem;
`;

export const linkedInState = uuid();

const LinkedIn = ({ isRequired, nodeId }) => {
    const dispatch = useDispatch();
    const formId = useContext(FormIdContext);

    useEffect(() => {
        const receiveMessage = (event) => {
            const eventFrom = get(event, 'data.from');
            const errorMessage = get(event, 'data.errorMessage');

            const userData = get(event, 'data.userData');
            const linkedinReceivedState = get(event, 'data.state');

            const canSetUserData = userData && linkedinReceivedState === linkedInState;

            /** This check is needed here instead of the epic filter
             * in order to prevent the devtools from spamming this dispatch
             * as they send messages constantly
             */
            if (eventFrom === 'LinkedIn') {
                dispatch(
                    linkedInReceiveMessage({
                        nodeId,
                        userData,
                        errorMessage,
                        canSetUserData,
                        formId,
                    }),
                );
            }
        };

        window.addEventListener('message', receiveMessage, false);

        return () => window.removeEventListener('message', receiveMessage, false);
    }, [nodeId, formId]);

    const openLinkedInModal = (event) => {
        event.preventDefault();
        const bootstrap = getBootstrap();
        const clientId = get(bootstrap, 'data.linkedInClientId');
        const pathUri = get(bootstrap, 'data.linkedInRedirectUri', '');
        const pathDomain = get(bootstrap, 'data.linkedInApprovedDomain', '');
        const redirectUrl = `${pathDomain}${pathUri}`;
        const authCodeUrl = get(bootstrap, 'data.linkedInAuthCodeUrl', '');
        // eslint-disable-next-line max-len
        const url = `${authCodeUrl}&client_id=${clientId}&redirect_uri=${redirectUrl}&state=${linkedInState}`;

        window.open(url, BLANK, WINDOW_DIMENTIONS);
    };

    return (
        <LinkedInButton
            data-qa={`${BASE_COMPONENT_NAME}__button`}
            onClick={openLinkedInModal}
            aria-required={isRequired || undefined}
        >
            <LinkedInIconWrapper>
                <LinkedInIcon />
            </LinkedInIconWrapper>
            {LINKED_IN_LABEL}
        </LinkedInButton>
    );
};

LinkedIn.propTypes = {
    nodeId: PropTypes.string.isRequired,
    isRequired: PropTypes.bool.isRequired,
};

export default LinkedIn;
